
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import Themeable from 'vuetify/lib/mixins/themeable';
import {
  ChatInterface,
  CommentCreateInterface,
  ProfileMapInterface,
  ReviewCommentCreateInterface,
  ChatReviewFormDataInterface,
} from '@/services/QC-V2/chat/interfaces';
import { CommentRate, CommentRepositoryInterface } from '@/services/QC-V2/comment/interfaces';
import { Roles } from '@/services/Roles';
import DeleteCommentDialog from '@/components/dialogs/qc-v2/comment/DeleteCommentDialog.vue';

@Component({
  components: { DeleteCommentDialog },
  mixins: [
    Themeable,
  ],
  inject: [
    'profileRepository',
    'commentRepositoryV2',
  ],
})
export default class ChatComments extends Vue {
  @Prop() private chat!: ChatInterface;
  @Prop() private reviewedId!: string;
  @Prop() private profilesMap!: ProfileMapInterface;
  @Prop({ default: false }) private disabled!: boolean;
  @Prop() private formData!: { [userId: string]: ChatReviewFormDataInterface };

  private reviewIndex: number = -1;

  private loading: boolean = true;

  private commentRepositoryV2!: CommentRepositoryInterface;

  private commentRates: typeof CommentRate = CommentRate;
  private commentRate: CommentRate = CommentRate.NOT_RATED;
  private editCommentRate: CommentRate = CommentRate.NOT_RATED;
  private chatComment: string = '';

  private commentIndexForEdit: number = -1;
  private editCommentText: string = '';

  private userRoles: object = Roles;

  private deleteCommentDialogVisible: boolean = false;
  private deleteCommentDialogEvent!: string;
  private deleteCommentDialogData!: { chatId?: string, commentId?: number, index?: number };

  private async created(): Promise<void> {
    await this.getProfiles();
    this.searchReviewIndex();
    this.loading = false;
  }

  @Watch('chat')
  private async reloadData(): Promise<void> {
    this.loading = true;
    this.searchReviewIndex();
    await this.getProfiles();
    this.loading = false;
  }

  @Watch('reviewedId')
  private searchReviewIndex(): void {
    this.reviewIndex = this.chat.reviews
      .findIndex((review) => !review.deletedAt && review.reviewedId === this.reviewedId);
  }

  private userHasScopes(scope: string): boolean {
    return this.$store.getters.scopes.includes(scope);
  }

  private async createChatComment(): Promise<void> {
    this.chatComment = this.chatComment.trim();
    if (!this.chatComment) {
      return;
    }

    if (this.reviewedId !== 'chat') {
      this.createReviewComment();
      return;
    }

    const commentData: CommentCreateInterface = {
      author: this.$store.getters.id,
      text: this.chatComment,
      rate: this.commentRate,
      timestamp: new Date().toISOString(),
    };

    try {
      const comment = await this.commentRepositoryV2.createChatComment(this.chat.id, commentData);
      this.$successDialog.show('Comment Created Successfully!');
      this.chat.comments.push(comment);
      this.chatComment = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private createReviewComment(): void {
    try {
      const commentData: ReviewCommentCreateInterface = {
        author: this.$store.getters.id,
        text: this.chatComment,
        rate: this.commentRate,
        timestamp: new Date().toISOString(),
        addToDossier: true,
      };

      this.formData[this.reviewedId].comments.push(commentData);
      this.$successDialog.show('Comment Created Successfully!');

      this.searchReviewIndex();

      this.chatComment = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
      this.$emit('review-comment-added');
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async getProfiles(): Promise<void> {
    this.$emit('comment-profiles', this.profilesMap);
  }

  private showDeleteChatCommentDialog(chatId: string, commentId: number): void {
    this.deleteCommentDialogData = { chatId, commentId };
    this.deleteCommentDialogEvent = 'chat-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private showDeleteReviewCommentDialog(commentId: number | undefined, index: number): void {
    this.deleteCommentDialogData = { commentId, index };
    this.deleteCommentDialogEvent = 'review-comment-deleted';
    this.deleteCommentDialogVisible = true;
  }

  private async deleteComment(data: { chatId: string, commentId: number }): Promise<void> {
    try {
      await this.commentRepositoryV2.deleteChatComment(data.chatId, data.commentId);
      this.$successDialog.show('Comment Deleted Successfully!');
      this.chat.comments = this.chat.comments.filter((comment) => comment.id !== data.commentId);
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private showEditBox(index: number, commentText: string): void {
    if (this.commentIndexForEdit === index) {
      this.editCommentText = '';
      this.commentIndexForEdit = -1;
      return;
    }

    this.editCommentText = commentText;
    this.commentIndexForEdit = index;
  }

  private async editComment(chatId: string, commentId: number, commentIndex: number): Promise<void> {
    try {
      const commentData: ReviewCommentCreateInterface = {
        author: this.chat.comments[commentIndex].author,
        text: this.editCommentText,
        rate: this.commentRate,
        timestamp: this.chat.comments[commentIndex].timestamp,
      };

      await this.commentRepositoryV2.updateChatComment(chatId, commentId, commentData);
      this.$successDialog.show('Comment Edited Successfully!');
      this.chat.comments[commentIndex].text = this.editCommentText;

      this.commentIndexForEdit = -1;
      this.editCommentText = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async editReviewComment(
    commentId: number | undefined,
    index: number,
  ): Promise<void> {
    try {

      const commentData = {
        author: this.$store.getters.id,
        text: this.editCommentText,
        rate: this.editCommentRate,
        addToDossier: true,
        timestamp: new Date().toISOString(),
      };

      if (commentId) {
        await this.commentRepositoryV2
          .updateChatReviewComment(this.chat.reviews[this.reviewIndex].id, commentId, commentData);
      }

      this.formData[this.reviewedId].comments[index] = commentData;
      this.$successDialog.show('Comment Edited Successfully!');
      this.commentIndexForEdit = -1;
      this.editCommentText = '';
      this.commentRate = CommentRate.NOT_RATED;
      this.editCommentRate = CommentRate.NOT_RATED;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async deleteReviewComment(data: { commentId: number, index: number }): Promise<void> {
    try {
      this.formData[this.reviewedId].comments.splice(data.index, 1);
      if (data.commentId) {
        await this.commentRepositoryV2.deleteChatReviewComment(this.chat.reviews[this.reviewIndex].id, data.commentId);
      }
      this.$successDialog.show('Comment Deleted Successfully!');
      this.$forceUpdate();
      this.deleteCommentDialogVisible = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private setCommentRate(rate: CommentRate): void {
    if (rate === this.commentRate) {
      this.commentRate = CommentRate.NOT_RATED;
      return;
    }

    this.commentRate = rate;
  }

  private setEditCommentRate(rate: CommentRate): void {
    if (rate === this.editCommentRate) {
      this.editCommentRate = CommentRate.NOT_RATED;
      return;
    }

    this.editCommentRate = rate;
  }
}
